<template>
  <div class="my-10">
    <h1 class="font-bold text-2xl">{{ $t('page.edit') }}</h1>
    <div v-if="originLoaded" class="mt-5 bg-white rounded p-5">
      <div class="grid grid-cols-3 gap-4 max-md:grid-cols-2 max-sm:grid-cols-1">
        <TextField
          v-if="categoryDetail?.has_name"
          ref="title"
          :defaultSingleValue="newPublication?.name"
          :label="$t('page.name')"
          :required="true"
          name="name"
        ></TextField>

        <TextField
          ref="phone"
          :defaultSingleValue="newPublication?.phone"
          :label="$t('form.phone')"
          :required="true"
          name="phone"
          placeholder="+993"
        ></TextField>

        <NumberField
          v-if="categoryDetail?.has_price"
          ref="price"
          :defaultSingleValue="newPublication?.price"
          :label="$t('page.price')"
          :required="!!categoryDetail?.is_price_required"
          name="price"
        ></NumberField>

        <template v-for="(field, index) in ogProperties" :key="index">
          <component
            :is="getComponent(field.data_type)"
            :ref="field.id"
            :field="field"
            :label="field.name"
            :name="field.name"
            :options="field.values"
            :property="field.id"
            :defaultValues="newPublication?.select_values"
            :defaultSelectValue="newPublication?.select_values?.find(el => el.property == field.id)"
            :defaultSingleValue="getSanitizedValue(field)"
            :defaultParentMultiValues="
              newPublication?.select_values?.filter(el => el.property == field.id)
            "
            :defaultChildrenValues="
              (() => {
                const filteredParents =
                  newPublication?.select_values?.filter(el => el.property === field.id) || [];
                const parentIds = filteredParents.map(parent => parent.id);
                return (
                  newPublication?.select_values?.filter(value =>
                    parentIds.includes(value.parent?.id),
                  ) || []
                );
              })()
            "
            :required="field.is_required"
          >
          </component>
        </template>

        <LocationNestedSelectField
          v-if="publicationLocations && categoryDetail?.has_address"
          ref="address"
          :defaultChildValue="newPublication?.district_id"
          :defaultParentValue="newPublication?.location_id"
          :label="$t('form.address')"
          :options="publicationLocations"
          name="district"
        ></LocationNestedSelectField>
      </div>

      <div class="flex flex-col mt-4">
        <div class="flex flex-col gap-2">
          <label class="font-semibold" for="message"
            >{{ $t('page.info') }}<span class="text-red-500">*</span></label
          >
          <Field
            id="description"
            v-model="formData.description"
            :maxlength="$store?.setting?.max_publication_description_length"
            as="textarea"
            class="w-full rounded border px-8 py-5 placeholder-[#5F5F5F] resize-none max-md:py-4"
            cols="30"
            name="description"
            rows="6"
          >
          </Field>
          <ErrorMessage class="text-red-500" name="description"></ErrorMessage>
        </div>

        <!-- ✅ THUMBNAIL PICTURE (строго отдельно от фото/видео) -->
        <div v-if="categoryDetail?.has_thumbnail_picture" class="w-full flex flex-col gap-2 mt-4">
          <p class="font-semibold">
            {{ $t('page.thumbnailPicture') }}
            <span class="text-red-500" v-show="categoryDetail?.is_thumbnail_picture_required"
              >*</span
            >
          </p>

          <div class="flex items-center gap-4">
            <input
              ref="thumbnailInput"
              class="hidden"
              type="file"
              accept=".jpg,.jpeg,.png"
              @change="onThumbnailChange"
            />

            <button
              type="button"
              class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
              @click="$refs.thumbnailInput.click()"
              :disabled="loadingThumbnail"
            >
              <IconUpload />
              {{ $t('page.uploadImage') }}
            </button>

            <InlineLoader class="!w-8 !h-8" :showLoader="loadingThumbnail"></InlineLoader>

            <button
              v-if="thumbnailId"
              type="button"
              class="bg-[#EB5757] text-white px-4 py-2 rounded hover:opacity-90"
              @click="removeThumbnail"
            >
              {{ $t('page.remove') }}
            </button>
          </div>

          <div v-if="thumbnailPreview" class="mt-2">
            <img
              :src="thumbnailPreview"
              class="h-28 w-48 object-contain rounded border"
              alt="thumb-preview"
            />
          </div>
        </div>

        <template v-if="categoryDetail?.has_image">
          <div class="w-full flex flex-col gap-2 mt-4">
            <p for="additionalinfo" class="font-semibold">
              {{ $t('page.uploadImage') }} ({{ imageFiles.length }}/{{ getMaxImageCount }})
              <span class="text-red-500" v-show="categoryDetail?.is_image_required">*</span>
            </p>
            <div
              @dragover="dragoverImage"
              @dragleave="dragleaveImage"
              @drop="dropImage"
              class="flex justify-center items-center flex-col border-4 border-dashed border-ml-input-100 p-20"
              :class="{ 'bg-ml-input-200': isDraggingImage }"
            >
              <input
                :disabled="imageFiles.length >= getMaxImageCount"
                ref="imageFile"
                name="files"
                class="hidden"
                type="file"
                multiple
                id="imageFileUpload"
                @change="onImageFileChange"
                accept=".jpg,.jpeg,.png"
              />
              <InlineLoader class="!w-8 !h-8" :showLoader="loadingImage"></InlineLoader>
              <label
                v-if="!loadingImage"
                class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
                for="imageFileUpload"
              >
                <template v-if="imageFiles.length < getMaxImageCount">
                  <IconUpload></IconUpload>{{ $t('page.uploadImage') }}
                </template>
                <template v-else>
                  <p class="text-blue-500">
                    {{ $t('page.maxImage') }}
                  </p>
                </template>
              </label>
            </div>
          </div>
        </template>

        <div
          class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1"
          v-if="imageFiles.length"
        >
          <div
            v-for="(file, idx) in imageFiles"
            :key="(file.detail?.name || file.detail?.id || file.path) + '-' + idx"
            class="w-full flex items-center justify-between p-2 gap-4 border-t border-b"
          >
            <img :src="file.path" alt="uploaded-image" class="h-20 w-32 object-fit" />
            <div class="flex items-center gap-2">
              <input
                type="radio"
                class="scale-150"
                name="main-image"
                :id="(file.detail?.name || file.detail?.id || file.path || idx) + '-' + idx"
              />
              <label :for="(file.detail?.name || file.detail?.id || file.path || idx) + '-' + idx">
                {{ file.detail?.name || file.detail?.id || 'file' }}
              </label>
            </div>
            <button
              class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded"
              type="button"
              @click="removeImage(idx)"
              title="Remove file"
            >
              <IconTrash></IconTrash>
            </button>
          </div>
        </div>

        <div class="w-full flex flex-col gap-2 mt-4">
          <p for="additionalinfo" class="font-semibold">
            {{ $t('page.uploadVideo') }} ({{ videoFiles.length }}/{{ getMaxVideoCount }})
          </p>
          <div
            @dragover="dragoverVideo"
            @dragleave="dragleaveVideo"
            @drop="dropVideo"
            class="flex justify-center items-center flex-col border-4 border-dashed border-ml-input-100 p-20"
            :class="{ 'bg-ml-input-200': isDraggingVideo }"
          >
            <input
              :disabled="videoFiles.length >= getMaxVideoCount"
              ref="videoFile"
              name="files"
              class="hidden"
              type="file"
              multiple
              id="videoFileUpload"
              @change="onVideoFileChange"
              accept=".mp4,.mov,.avi,.mkv,.webm"
            />
            <InlineLoader class="!w-8 !h-8" :showLoader="loadingVideo"></InlineLoader>
            <label
              v-if="!loadingVideo"
              class="flex gap-3 items-center bg-ml-secondary text-main-color rounded-md cursor-pointer py-3 px-5 hover:bg-ml-main active:bg-ml-main"
              for="videoFileUpload"
            >
              <template v-if="videoFiles.length < getMaxVideoCount">
                <IconUpload></IconUpload>{{ $t('page.uploadVideo') }}
              </template>
              <template v-else>
                <p class="text-blue-500">
                  {{ $t('page.maxVideo') }}
                </p>
              </template>
            </label>
          </div>
        </div>

        <div
          class="grid grid-cols-3 gap-5 mt-4 max-lg:grid-cols-2 max-md:grid-cols-1"
          v-if="videoFiles.length"
        >
          <div
            v-for="(file, idx) in videoFiles"
            :key="(file.detail?.name || file.detail?.id || file.path) + '-' + idx"
            class="w-full flex items-center justify-between p-2 gap-4 border-t border-b"
          >
            <IconMedia class="w-20 h-20"></IconMedia>
            <div class="flex items-center gap-2">
              <label>
                {{ file.detail?.name || file.detail?.id || 'file' }}
              </label>
            </div>
            <button
              class="ml-3 mt-1 bg-[#EB5757] p-2.5 rounded"
              type="button"
              @click="removeVideo(idx)"
              title="Remove file"
            >
              <IconTrash></IconTrash>
            </button>
          </div>
        </div>
      </div>

      <div class="my-4">
        <div class="grid grid-cols-3 my-6">
          <SwitchField
            v-if="categoryDetail?.on_credit"
            ref="on_credit"
            :defaultSingleValue="newPublication?.on_credit"
            :label="$t('page.onCredit')"
            name="on_credit"
          />
          <SwitchField
            v-if="categoryDetail?.on_exchange"
            ref="on_exchange"
            :defaultSingleValue="newPublication?.on_exchange"
            :label="$t('page.onExchange')"
            name="on_exchange"
          />
          <SwitchField
            ref="allow_comments"
            :defaultSingleValue="newPublication?.allow_comments"
            :label="$t('page.allowComments')"
            name="allow_comments"
          />
        </div>
      </div>

      <div>
        <SubmitBtn :loading="loading" @click="onSubmitForm">{{ $t('page.addEdit') }}</SubmitBtn>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import TextField from '@cmp/TextField.vue';
import NumberField from '@cmp/NumberField.vue';
import SwitchField from '@cmp/SwitchField.vue';
import SelectFieldEdit from '@cmp/SelectFieldEdit.vue';
import MultiSelectFieldEdit from '@cmp/MultiSelectFieldEdit.vue';
import NestedSelectFieldEdit from '@cmp/NestedSelectFieldEdit.vue';
import LocationNestedSelectField from '@cmp/LocationNestedSelectField.vue';
import MultiNestedSelectFieldEdit from '@cmp/MultiNestedSelectFieldEdit.vue';
import NestedMultiSelectFieldEdit from '@cmp/NestedMultiSelectFieldEdit.vue';
import Multiselect from '@vueform/multiselect';
import IconUpload from '@icons/IconUpload.vue';
import IconTrash from '@icons/IconTrash.vue';
import InlineLoader from '@cmp/InlineLoader.vue';
import IconMedia from '@icons/IconMedia.vue';

import { ErrorMessage, Field, Form } from 'vee-validate';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/i18n';
import { isVideo } from '@/helpers';
import { mapState } from 'pinia';
import { useAppStore } from '@/store';

// ✅ nested cache как в рабочем editMyPublication.vue
import { getNestedSelectValues } from '@/helpers/nestedSelectCache';

export default {
  name: 'editMyBusinessPublication',

  components: {
    TextField,
    SwitchField,
    MultiSelectFieldEdit,
    MultiNestedSelectFieldEdit,
    SelectFieldEdit,
    NumberField,
    Multiselect,
    NestedSelectFieldEdit,
    LocationNestedSelectField,
    Field,
    Form,
    ErrorMessage,
    IconMedia,
    IconUpload,
    InlineLoader,
    IconTrash,
    NestedMultiSelectFieldEdit,
  },

  setup() {
    useI18n(); // просто чтобы не было лишних изменений
    const childComponents = ref([]);
    const on_credit = ref();
    const on_exchange = ref();
    const allow_comments = ref();
    const price = ref();
    const phone = ref();
    const title = ref();
    const address = ref();

    return {
      childComponents,
      on_credit,
      on_exchange,
      allow_comments,
      price,
      phone,
      title,
      address,
    };
  },

  data() {
    return {
      // ✅ thumbnail state (как в рабочем)
      thumbnailFile: null,
      thumbnailPreview: null,
      thumbnailId: null,
      loadingThumbnail: false,

      originLoaded: false,
      originId: null,
      originPublication: {},
      newPublication: {},
      businessAccountDetail: {},
      loading: false,
      loadingImage: false,
      loadingVideo: false,
      currentCategory: null,
      ogProperties: [],
      properties: [],
      publicationLocations: [],
      isDraggingVideo: false,
      isDraggingImage: false,
      imageFiles: [],
      videoFiles: [],
      imageFileUrls: [],
      videoFileUrls: [],
      imageFileIds: [],
      videoFileIds: [],

      categoryDetail: {},
      groups: {
        mode: 'tags',
        value: [],
        options: [],
        searchable: true,
        createTag: true,
      },
      formData: {
        category: null,
        origin: 0,
        district: 0,
        on_exchange: false,
        on_credit: false,
        description: '',
        allow_comments: false,
        price: 0,
        name: '',
        phone: '',
        images: [],
        thumbnail_picture: null,
        values: [],
        select_values: [],
        business_account: null,
      },
    };
  },

  methods: {
    isVideo,

    // ✅ как в рабочем: санитайз значений динамических полей (чтобы number не ломался от "км")
    getSanitizedValue(field) {
      const raw = this.newPublication?.attributes?.[field.name];

      // TextField
      if (field.data_type === 0) {
        if (raw === null || raw === undefined) return '';
        return String(raw);
      }

      // NumberField
      if ([1, 2].includes(field.data_type)) {
        if (raw === null || raw === undefined || raw === '') return null;
        const value = parseFloat(String(raw).replace(/[^\d.]/g, ''));
        return isNaN(value) ? null : value;
      }

      if (raw === null || raw === undefined) return null;
      return raw;
    },

    async validateDynamicFields() {
      const isValid = [];
      const fields = [];
      const state = { valid: true, fields: [] };

      for (let i = 0; i < this.childComponents.length; i++) {
        const key = this.childComponents[i];
        const refEl = Array.isArray(this.$refs[key]) ? this.$refs[key][0] : this.$refs[key];
        if (!refEl?.validateField) continue;

        const res = await refEl.validateField();

        fields.push({
          valid: !!res?.valid,
          mode: res?.mode ?? refEl?.mode ?? 'single',
          property: res?.property ?? refEl?.property ?? key,
          value: res?.value,
        });

        isValid.push(!!res?.valid);
      }

      if (isValid.includes(false)) state.valid = false;
      state.fields = fields;
      return state;
    },

    // -------------------------
    // ✅ THUMBNAIL METHODS (как в рабочем)
    // -------------------------
    async onThumbnailChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      if (this.thumbnailPreview) URL.revokeObjectURL(this.thumbnailPreview);

      this.thumbnailFile = file;
      this.thumbnailPreview = URL.createObjectURL(file);
      this.thumbnailId = null;

      try {
        this.loadingThumbnail = true;

        const fd = new FormData();
        fd.append('path', file);

        const res = await api.post('files/medias/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res?.status === 201) {
          this.thumbnailId = res.data.id;
          if (this.$refs.thumbnailInput) this.$refs.thumbnailInput.value = '';
        } else {
          this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          this.thumbnailPreview = null;
          this.thumbnailFile = null;
        }
      } catch (err) {
        console.log(err);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
        this.thumbnailPreview = null;
        this.thumbnailFile = null;
      } finally {
        this.loadingThumbnail = false;
      }
    },

    removeThumbnail() {
      if (this.thumbnailPreview) URL.revokeObjectURL(this.thumbnailPreview);

      this.thumbnailId = null;
      this.thumbnailFile = null;
      this.thumbnailPreview = null;

      if (this.$refs.thumbnailInput) this.$refs.thumbnailInput.value = '';
    },

    // -------------------------
    // Images
    // -------------------------
    async dropImage(e) {
      e.preventDefault();
      await this.onImageFileChange(e.dataTransfer.files);
      this.isDraggingImage = false;
    },
    removeImage(i) {
      const f = this.imageFiles[i];
      if (f?.path?.startsWith('blob:')) URL.revokeObjectURL(f.path);
      this.imageFiles.splice(i, 1);
      this.imageFileIds.splice(i, 1);
    },
    dragoverImage(e) {
      e.preventDefault();
      this.isDraggingImage = true;
    },
    dragleaveImage() {
      this.isDraggingImage = false;
    },
    async onImageFileChange(eOrFiles) {
      const files = eOrFiles?.target?.files || eOrFiles || this.$refs.imageFile?.files || [];
      const remaining = this.getMaxImageCount - this.imageFiles.length;

      if (!files || !files.length) return;

      if (remaining <= 0) {
        this.$toast.info(`${this.$t('page.maxImage')} (${this.getMaxImageCount})`);
        if (eOrFiles?.target && this.$refs.imageFile) this.$refs.imageFile.value = '';
        return;
      }

      const slice = Array.from(files).slice(0, remaining);
      if (slice.length < files.length) {
        this.$toast.info(`${this.$t('page.maxImage')} (${this.getMaxImageCount})`);
      }

      this.loadingImage = true;
      try {
        for (const f of slice) {
          const fd = new FormData();
          fd.append('path', f);

          const res = await api.post('files/medias/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (res?.status === 201) {
            this.imageFiles.push({ detail: f, path: URL.createObjectURL(f), isUploaded: true });
            this.imageFileIds.push(res.data.id);
          } else {
            this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          }
        }
      } catch (e) {
        console.log(e);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
      } finally {
        this.loadingImage = false;
        if (eOrFiles?.target && this.$refs.imageFile) this.$refs.imageFile.value = '';
      }
    },

    // -------------------------
    // Video
    // -------------------------
    dragoverVideo(e) {
      e.preventDefault();
      this.isDraggingVideo = true;
    },
    dragleaveVideo() {
      this.isDraggingVideo = false;
    },
    async dropVideo(e) {
      e.preventDefault();
      await this.onVideoFileChange(e.dataTransfer.files);
      this.isDraggingVideo = false;
    },
    removeVideo(i) {
      const f = this.videoFiles[i];
      if (f?.path && typeof f.path === 'string' && f.path.startsWith('blob:')) {
        URL.revokeObjectURL(f.path);
      }
      this.videoFiles.splice(i, 1);
      this.videoFileIds.splice(i, 1);
    },
    async onVideoFileChange(eOrFiles) {
      const files = eOrFiles?.target?.files || eOrFiles || this.$refs.videoFile?.files || [];
      const remaining = this.getMaxVideoCount - this.videoFiles.length;

      if (!files || !files.length) return;

      if (remaining <= 0) {
        this.$toast.info(`${this.$t('page.maxVideo')} (${this.getMaxVideoCount})`);
        if (eOrFiles?.target && this.$refs.videoFile) this.$refs.videoFile.value = '';
        return;
      }

      const slice = Array.from(files).slice(0, remaining);

      if (slice.length < files.length) {
        this.$toast.info(`${this.$t('page.maxVideo')} (${this.getMaxVideoCount})`);
      }

      this.loadingVideo = true;
      try {
        for (const f of slice) {
          const fd = new FormData();
          fd.append('path', f);

          const res = await api.post('files/medias/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (res?.status === 201) {
            this.videoFiles.push({
              detail: f,
              path: URL.createObjectURL(f),
              isUploaded: true,
            });
            this.videoFileIds.push(res.data.id);
          } else {
            this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
          }
        }
      } catch (e) {
        console.log(e);
        this.$toast.warning(i18n.global.t('warning.UPLOAD_FAILED'));
      } finally {
        this.loadingVideo = false;
        if (eOrFiles?.target && this.$refs.videoFile) this.$refs.videoFile.value = '';
      }
    },

    // -------------------------
    // ✅ Properties / Select values (фикс пустых select)
    // -------------------------
    async getProperties(id) {
      this.loading = true;
      try {
        const response = await api.get(`attributes/properties/?publication_categories=${id}`);
        this.ogProperties = response.data;

        // ✅ грузим values строго по типам (как в рабочем)
        await Promise.all(
          this.ogProperties.map(async element => {
            this.childComponents.push(element.id);

            // nested / multi-nested / nested-multi
            if ([6, 7, 8].includes(element.data_type)) {
              element.values = await getNestedSelectValues(element.id);
              return;
            }

            // обычные select
            if ([4, 5].includes(element.data_type)) {
              element.values = await this.getSelectValues(element.id);
              return;
            }
          }),
        );
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async getSelectValues(property) {
      try {
        const response = await api.get(
          `attributes/select_values/?property=${property}&include_children=true`,
        );
        const data = response.data;
        // ✅ ВАЖНО: api часто отдаёт пагинацию
        return Array.isArray(data) ? data : data.results || [];
      } catch (e) {
        console.log(e);
        return [];
      }
    },

    async getPublicationLocations() {
      this.loading = true;
      try {
        const response = await api.get(`publications/locations/`);
        this.publicationLocations = response.data;

        this.publicationLocations.forEach(element => {
          this.getDistricts(element.id).then(elem => {
            element['values'] = elem;
          });
        });
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async getDistricts(location) {
      this.loading = true;
      try {
        const response = await api.get(`publications/districts/?location=${location}`);
        return response.data;
      } catch (e) {
        console.log(e);
        return [];
      } finally {
        this.loading = false;
      }
    },

    async onSubmitForm() {
      console.log('✅ onSubmitForm CLICKED');

      this.formData.values = [];
      this.formData.select_values = [];

      const normalizeBool = v => {
        if (v === true || v === false) return v;
        if (v === 1 || v === '1') return true;
        if (v === 0 || v === '0') return false;

        if (typeof v === 'string') {
          const s = v.trim().toLowerCase();
          if (['true', 'yes', 'on'].includes(s)) return true;
          if (['false', 'no', 'off', ''].includes(s)) return false;
        }
        return !!v;
      };

      const normalizeId = v => {
        if (v === undefined || v === null) return null;
        if (typeof v === 'object') return v.id ?? v.value ?? null;
        return v;
      };

      let address, price, on_exchange, on_credit, title;
      const isValid = [];

      const dynamicFields = await this.validateDynamicFields();

      const phone = await this.$refs.phone.validateField();
      const allow_comments = await this.$refs.allow_comments.validateField();
      isValid.push(phone.valid, allow_comments.valid);

      if (this.categoryDetail?.has_address && this.$refs.address?.validateField) {
        address = await this.$refs.address.validateField();
        isValid.push(address.valid);
      }
      if (this.categoryDetail?.has_name && this.$refs.title?.validateField) {
        title = await this.$refs.title.validateField();
        isValid.push(title.valid);
      }
      if (this.categoryDetail?.has_price && this.$refs.price?.validateField) {
        price = await this.$refs.price.validateField();
        isValid.push(price.valid);
      }
      if (this.categoryDetail?.on_exchange && this.$refs.on_exchange?.validateField) {
        on_exchange = await this.$refs.on_exchange.validateField();
        isValid.push(on_exchange.valid);
      }
      if (this.categoryDetail?.on_credit && this.$refs.on_credit?.validateField) {
        on_credit = await this.$refs.on_credit.validateField();
        isValid.push(on_credit.valid);
      }

      // ✅ THUMBNAIL REQUIRED
      const thumbnailRequired =
        !!this.categoryDetail?.has_thumbnail_picture &&
        !!this.categoryDetail?.is_thumbnail_picture_required;

      if (thumbnailRequired && !this.thumbnailId) {
        this.$toast.error(i18n.global.t('warning.THUMBNAIL_REQUIRED'));
        return;
      }

      if (!dynamicFields?.valid || isValid.includes(false)) {
        this.$toast.error(
          i18n.global.t('warning.REQUIRED_MESSAGE') || 'Заполните обязательные поля',
        );
        console.log('[EDIT BA] isValid:', isValid);
        console.log('[EDIT BA] dynamicFields:', dynamicFields);
        return;
      }

      this.loading = true;
      try {
        const fields = dynamicFields.fields || [];
        for (let i = 0; i < fields.length; i++) {
          const el = fields[i];
          const raw = el?.value;
          const normalized = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw;

          if (el.mode === 'single') {
            if (
              normalized !== null &&
              normalized !== undefined &&
              String(normalized).trim() !== ''
            ) {
              this.formData.values.push({
                name: String(normalized).trim(),
                property: Number(el.property),
              });
            }
            continue;
          }

          if (normalized !== null && normalized !== undefined) {
            const arr = Array.isArray(normalized) ? normalized : [normalized];
            this.formData.select_values.push(...arr);
          }
        }

        this.formData.select_values = Array.from(
          new Set(
            (this.formData.select_values || [])
              .flat()
              .map(v => normalizeId(v))
              .filter(v => v !== null && v !== undefined && v !== '')
              .map(Number)
              .filter(Number.isFinite),
          ),
        );

        if (this.categoryDetail?.has_name) {
          this.formData.name = title?.value ?? '';
        } else {
          this.formData.name = this.formData.name ?? '';
        }

        this.formData.phone = phone.value;

        if (this.categoryDetail?.has_price) {
          const pv = price?.value;
          this.formData.price = pv === '' || pv === undefined ? null : pv;
        } else {
          this.formData.price = this.formData.price ?? null;
        }

        this.formData.allow_comments = normalizeBool(allow_comments.value);

        this.formData.on_exchange = this.categoryDetail?.on_exchange
          ? normalizeBool(on_exchange?.value)
          : false;

        this.formData.on_credit = this.categoryDetail?.on_credit
          ? normalizeBool(on_credit?.value)
          : false;

        this.formData.origin = this.originId;

        if (this.categoryDetail?.has_address) {
          this.formData.district = address?.value?.[1] ?? 0;
        } else {
          this.formData.district = this.formData.district ?? 0;
        }

        this.formData.category = this.newPublication?.category_id;

        this.formData.business_account = this.newPublication?.business_account?.id
          ? Number(this.newPublication.business_account.id)
          : null;

        // ✅ images — только фото+видео
        this.formData.images = [...this.imageFileIds, ...this.videoFileIds];

        // ✅ thumbnail_picture — отдельно
        this.formData.thumbnail_picture = this.categoryDetail?.has_thumbnail_picture
          ? this.thumbnailId
          : null;

        const response = await api.post(`publications/my-publication-requests/`, this.formData);

        // ✅ 200 = изменения применились сразу в Publication
        if (response.status === 200) {
          this.$toast.success(i18n.global.t('success.SAVED'));
          this.$router.push({ name: 'myBusinessPublications' });
          return;
        }

        // ✅ 201 = создан PublicationRequest и нужна модерация
        if (response.status === 201) {
          this.$toast.info(i18n.global.t('warning.PUBLICATION_EDITED'));
          this.$router.push({ name: 'businessPublicationRequests' });
          return;
        }

        // запасной вариант
        this.$toast.info(i18n.global.t('warning.PUBLICATION_EDITED'));
        this.$router.push({ name: 'businessPublicationRequests' });
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async getMyPublication() {
      try {
        const response = await api.get(`publications/my-publications/${this.originId}/`);
        this.originPublication = response.data;
        this.newPublication = response.data;

        this.formData.business_account = this.newPublication?.business_account?.id ?? null;

        // ✅ thumbnail existing
        const tp = this.newPublication?.thumbnail_picture;
        if (tp?.id) {
          this.thumbnailId = tp.id;
          this.thumbnailPreview = this.newPublication?.thumbnail || tp?.path || null;
        }

        this.formData.description = this.newPublication?.description || '';

        // ✅ BA detail (лимиты по подписке)
        const baId = this.newPublication?.business_account?.id;
        if (baId) {
          try {
            const baRes = await api.get(`posts/my-business-accounts/${baId}/`);
            this.businessAccountDetail = baRes.data;
          } catch (e) {
            console.log(e);
          }
        } else {
          this.businessAccountDetail = {};
        }

        // медиа
        this.imageFiles = [];
        this.videoFiles = [];
        this.imageFileIds = [];
        this.videoFileIds = [];

        (this.newPublication.images || []).forEach(e => {
          if (this.isVideo(e.path)) {
            this.videoFileIds.push(e.id);
            this.videoFiles.push({ detail: { name: e.id }, path: e.path, isUploaded: true });
          } else {
            this.imageFileIds.push(e.id);
            this.imageFiles.push({ detail: { name: e.id }, path: e.path, isUploaded: true });
          }
        });

        await this.getCategoryDetail();

        this.originLoaded = true;

        this.getProperties(this.originPublication.category_id);
      } catch (e) {
        console.log(e);
      }
    },

    async getCategoryDetail() {
      try {
        const response = await api.get(
          `publications/categories/${this.originPublication.category_id}/`,
        );
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },

    getComponent(type) {
      switch (type) {
        case 0:
          return TextField;
        case 1:
          return NumberField;
        case 2:
          return NumberField;
        case 3:
          return SwitchField;
        case 4:
          return SelectFieldEdit;
        case 5:
          return MultiSelectFieldEdit;
        case 6:
          return NestedSelectFieldEdit;
        case 7:
          return MultiNestedSelectFieldEdit;
        case 8:
          return NestedMultiSelectFieldEdit;
        default:
          return TextField;
      }
    },
  },

  computed: {
    ...mapState(useAppStore, ['getSubscriptionSettings']),
    getConfig() {
      return this.getSubscriptionSettings.find(
        el => el.name === this.businessAccountDetail?.subscription,
      );
    },
    getMaxImageCount() {
      return this.getConfig?.business_account_publication_image_count !== undefined
        ? this.getConfig?.business_account_publication_image_count
        : this.$store.setting.max_business_publication_image_count;
    },
    getMaxVideoCount() {
      return this.getConfig?.business_account_publication_video_count !== undefined
        ? this.getConfig?.business_account_publication_video_count
        : this.$store.setting.max_business_publication_video_count;
    },
  },

  created() {
    this.originId = this.$route.params.id;
    this.getPublicationLocations();
    this.getMyPublication();
  },
};
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
